import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { App } from "./App";
import { buildTheme } from "./theme";
import { useColorMode } from "@/store/colorMode";
import { useDensity } from "@/store/density";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { GlobalToast } from "@/components/GlobalToast";
import { notify } from "@/store/toast";

const queryClient = new QueryClient({
  // Opt-in toasts: any mutation that sets meta.successMessage gets a success snackbar.
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      const msg = mutation.meta?.successMessage as string | undefined;
      if (msg) notify.success(msg);
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// PWA service worker: register only in production builds. In dev, a service
// worker caches the app shell and serves a stale bundle (breaks live reload), so
// we proactively unregister any existing SW and clear its caches.
if ("serviceWorker" in navigator) {
  if (import.meta.env.PROD) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
  } else {
    navigator.serviceWorker.getRegistrations().then((regs) => regs.forEach((r) => r.unregister()));
    if (window.caches) caches.keys().then((keys) => keys.forEach((k) => caches.delete(k)));
  }
}

/** Subscribes to the color-mode store and rebuilds the MUI theme on toggle. */
function ThemedApp() {
  const mode = useColorMode((s) => s.mode);
  const density = useDensity((s) => s.density);
  const theme = useMemo(() => buildTheme(mode, density), [mode, density]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
      <GlobalToast />
      <PwaInstallBanner />
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
